import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { ATLAS } from '../data/atlasConfig.js';
import { ExternalLinkIcon, CarIcon, BottleIcon, FilterIcon } from './icons.jsx';
import { planTripRoute, googleMapsDirUrl, routeHash, fetchDrivingRoute } from '../utils/tripRoute.js';

// Winery Trip Planner sidebar (/winery-trip-planner, michigan only): pick a starting
// town and a number of winery stops, get a nearest-neighbor visiting
// order over the region's producers, then hand the drive to Google Maps.
// Idea from the local wine-route-finder reference app (gitignored).
export default function TripPlanner({ plannedRoute, onPlanChange }) {
  const trip = ATLAS.tripPlanner;
  const [producers, setProducers] = useState(null); // null=loading, []=none
  const [cityName, setCityName] = useState(trip.cities[0].name);
  const [numStops, setNumStops] = useState(3);
  const [planning, setPlanning] = useState(false);
  // what the current plan was made from — unchanged inputs disable the button
  const [plannedKey, setPlannedKey] = useState(null);
  // include/exclude wineries from routing (all included by default)
  const [excluded, setExcluded] = useState(() => new Set());
  const [wineriesOpen, setWineriesOpen] = useState(false);
  // town picker: same pattern as the sidebar's title-anchored region
  // picker (floating rounded card, legend-list rows, pin on selection)
  const [townOpen, setTownOpen] = useState(false);
  const townRef = useRef(null);
  const wineriesRef = useRef(null);
  useEffect(() => {
    if (!wineriesOpen) return;
    const onDown = (e) => { if (wineriesRef.current && !wineriesRef.current.contains(e.target)) setWineriesOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [wineriesOpen]);
  useEffect(() => {
    if (!townOpen) return;
    const onDown = (e) => { if (townRef.current && !townRef.current.contains(e.target)) setTownOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [townOpen]);

  useEffect(() => {
    let cancel = false;
    supabase
      .rpc('producers_by_bereich', { p_anbaugebiet: trip.region, p_bereich: null })
      .then((r) => { if (!cancel) setProducers(Array.isArray(r.data) ? r.data : []); });
    return () => { cancel = true; };
  }, [trip.region]);

  const city = useMemo(() => trip.cities.find((c) => c.name === cityName) ?? trip.cities[0], [trip, cityName]);

  // Plan: pick stops (nearest-neighbor), then resolve REAL driving
  // geometry — the trip_routes cache first (seeded from the previous
  // planner; keyed by city+count+winery set), one Mapbox Directions
  // call on a miss (saved back for the next person). If both fail the
  // route still works with straight dashed legs.
  const pool = useMemo(
    () => (producers ?? []).filter((p) => !excluded.has(p.name)),
    [producers, excluded],
  );
  const toggleWinery = (name) => setExcluded((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const inputsKey = `${cityName}|${numStops}|${[...excluded].sort().join(',')}`;
  const plan = async () => {
    const stops = planTripRoute(city, pool, numStops);
    if (!stops.length) return;
    setPlanning(true);
    const cityLabel = `${city.name}${trip.citySuffix ?? ''}`;
    const hash = routeHash(cityLabel, stops.length, stops.map((p) => p.name));
    const byName = new Map((producers ?? []).map((p) => [p.name, p]));
    let result = { start: { ...city }, route: stops, geometry: null, distanceM: null };
    try {
      // city+count first: the seeded routes were planned with driving
      // distances, so their winery SETS rarely match our straight-line
      // pick — an exact-hash lookup alone would strand them. Any stored
      // route for this combo IS the curated answer.
      let cached = null;
      if (excluded.size === 0) {
        const byCombo = await supabase.from('trip_routes')
          .select('route_geometry, waypoints, total_distance')
          .eq('start_city', cityLabel).eq('winery_count', stops.length)
          .order('created_at', { ascending: false }).limit(1);
        if (byCombo.data?.[0]) cached = byCombo.data[0];
      }
      if (!cached) {
        const { data } = await supabase.rpc('trip_route_get', { p_hash: hash });
        cached = data;
      }
      if (cached?.route_geometry?.length && cached?.waypoints?.length) {
        result.route = cached.waypoints.map((w) => ({
          ...(byName.get(w.name) ?? {
            id: w.id ?? w.name, name: w.name, website_url: w.website ?? null,
            latitude: w.position?.[0], longitude: w.position?.[1],
          }),
          // seeded routes carry street addresses — the Google handoff
          // uses them to hit the exact business listing
          gmaps_address: w.address ?? null,
        }));
        result.geometry = cached.route_geometry;
        result.distanceM = cached.total_distance != null ? Number(cached.total_distance) : null;
      } else {
        const driven = await fetchDrivingRoute(trip.mapboxToken, city, stops);
        if (driven) {
          result.geometry = driven.geometry;
          result.distanceM = driven.distanceM;
          supabase.rpc('trip_route_save', {
            p_hash: hash,
            p_start_city: cityLabel,
            p_count: stops.length,
            p_selected: stops.map((p) => p.name),
            p_geometry: driven.geometry,
            p_waypoints: stops.map((p) => ({
              id: p.name, name: p.name, website: p.website_url ?? null,
              position: [Number(p.latitude), Number(p.longitude)],
            })),
            p_distance: driven.distanceM,
          }).then(() => {});
        }
      }
    } catch { /* cache/directions optional — dashed fallback below */ }
    setPlanning(false);
    setPlannedKey(inputsKey);
    onPlanChange(result);
  };
  const clear = () => { setPlannedKey(null); onPlanChange(null); };

  const route = plannedRoute?.route ?? [];

  return (
    <aside className="sidebar trip-planner">
      <header className="detail__header">
        <div className="detail__title-wrap">
          <p className="sidebar__sub sidebar__sub--above trip-planner__above">{trip.region} {ATLAS.terms.region}</p>
          <h2 className="detail__title">
            <span className="detail__title-name">Winery Trip Planner</span>
          </h2>
        </div>
      </header>
      <div className="detail__body trip-planner__body">
        <div className="detail__read-section">
          <h4 className="detail__read-heading trip-planner__heading" ref={wineriesRef}>
            Plan a route
            <button type="button" className={`trip-planner__filter-btn${wineriesOpen || excluded.size > 0 ? ' trip-planner__filter-btn--open' : ''}`} onClick={() => setWineriesOpen((o) => !o)} aria-expanded={wineriesOpen} title="Wineries" aria-label="Wineries">
              <FilterIcon size={13} />
              <span>Wineries{excluded.size > 0 ? ` (${excluded.size} off)` : ''}</span>
            </button>
            {wineriesOpen && (
              <div className="sidebar__pick-panel trip-planner__wineries-panel">
                <div className="trip-planner__wineries">
                  {(producers ?? []).map((p) => (
                    <label key={p.id} className="trip-planner__winery">
                      <input type="checkbox" checked={!excluded.has(p.name)} onChange={() => toggleWinery(p.name)} />
                      <span>{p.short_name || p.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </h4>
          <div className="trip-planner__form">
            <div className="trip-planner__field" ref={townRef}>
              <span className="trip-planner__label">Starting town</span>
              <div className="trip-planner__town">
                <button type="button" className="trip-planner__town-trigger" onClick={() => setTownOpen((o) => !o)} aria-expanded={townOpen}>
                  <span>{cityName}</span>
                  <svg className={`sidebar__title-caret ${townOpen ? 'sidebar__title-caret--up' : ''}`} width="11" height="8" viewBox="0 0 10 7"><polyline points="1,1 5,6 9,1" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </button>
                {townOpen && (
                  <div className="sidebar__pick-panel trip-planner__town-panel">
                    <ul className="legend-list">
                      {trip.cities.map((c) => {
                        const selected = c.name === cityName;
                        return (
                          <li key={c.name} className="legend-list__item" style={{ cursor: 'pointer' }}
                            onClick={() => { setTownOpen(false); setCityName(c.name); }}>
                            <span className="legend-list__swatch" style={{ visibility: 'hidden' }} />
                            {selected && (
                              <svg className="legend-list__pin legend-list__pin--name" viewBox="0 0 24 24" width="20" height="20" fill="#a8a29e"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"/></svg>
                            )}
                            <span>{c.name}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <label className="trip-planner__field">
              <span className="trip-planner__label">Winery stops: {numStops}</span>
              <input
                type="range" min="2" max="7" step="1" value={numStops}
                onChange={(e) => setNumStops(Number(e.target.value))}
              />
            </label>
            <button className="btn btn--primary trip-planner__plan" onClick={plan} disabled={!pool.length || planning || (plannedRoute != null && plannedKey === inputsKey)}>
              <CarIcon size={15} style={{ marginRight: '0.4rem', verticalAlign: '-2px' }} />
              {producers === null ? 'Loading wineries…' : planning ? 'Planning route…' : 'Plan my route'}
            </button>
          </div>
        </div>

        {route.length > 0 && (
          <div className="detail__read-section">
            <h4 className="detail__read-heading">Your route</h4>
            <div className="trip-planner__route">
              {route.map((p, i) => (
                <div key={p.id} className="trip-planner__stop">
                  <span className="trip-planner__badge">{i + 1}</span>
                  {trip.logos?.[p.name] ? (
                    <img className="trip-planner__logo" src={trip.logos[p.name]} alt="" />
                  ) : (
                    <span className="trip-planner__glass"><BottleIcon size={15} /></span>
                  )}
                  {p.website_url ? (
                    <a className="trip-planner__stop-name trip-planner__stop-name--link" href={p.website_url} target="_blank" rel="noopener noreferrer">
                      {p.short_name || p.name}<ExternalLinkIcon size={10} style={{ marginLeft: '0.3em' }} />
                    </a>
                  ) : (
                    <span className="trip-planner__stop-name">{p.short_name || p.name}</span>
                  )}
                </div>
              ))}
              <div className="detail__actions trip-planner__actions">
                <button className="btn btn--ghost" onClick={clear}>New route</button>
                <button
                  className="btn btn--ghost trip-planner__gmaps"
                  onClick={() => window.open(googleMapsDirUrl(plannedRoute.start, route), '_blank', 'noopener')}
                  title="Open in Google Maps" aria-label="Open in Google Maps"
                >
                  <img src="/google-maps-logo.svg" alt="Google Maps" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
